"use client"

import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react"

import type { PublicAuthDictionary, PublicAuthLocale } from "./types"

type PathValue<T, K extends string> = K extends `${infer A}.${infer B}`
  ? A extends keyof T
    ? PathValue<T[A], B>
    : never
  : K extends keyof T
    ? T[K]
    : never

function resolvePath<T extends object>(obj: T, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, part) => {
    if (!acc || typeof acc !== "object") return undefined
    return (acc as Record<string, unknown>)[part]
  }, obj)
}

type PublicAuthI18nContextValue = {
  locale: PublicAuthLocale
  dictionary: PublicAuthDictionary
  t: <K extends string>(key: K) => PathValue<PublicAuthDictionary, K>
}

const PublicAuthI18nContext = createContext<PublicAuthI18nContextValue | null>(null)

type PublicAuthI18nProviderProps = {
  locale: PublicAuthLocale
  dictionary: PublicAuthDictionary
  children: ReactNode
}

export function PublicAuthI18nProvider({ locale, dictionary, children }: PublicAuthI18nProviderProps) {
  const t = useMemo(
    () =>
      <K extends string>(key: K): PathValue<PublicAuthDictionary, K> => {
        const value = resolvePath(dictionary, key)
        return value as PathValue<PublicAuthDictionary, K>
      },
    [dictionary],
  )

  const value = useMemo(() => ({ locale, dictionary, t }), [locale, dictionary, t])

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  return <PublicAuthI18nContext.Provider value={value}>{children}</PublicAuthI18nContext.Provider>
}

export function usePublicAuthI18n() {
  const ctx = useContext(PublicAuthI18nContext)
  if (!ctx) {
    throw new Error("usePublicAuthI18n must be used within PublicAuthI18nProvider")
  }
  return ctx
}

/** Safe when offers render on authenticated pages without PublicAuthI18nProvider. */
export function useOptionalPublicAuthI18n() {
  return useContext(PublicAuthI18nContext)
}
